import { useEffect, useId, useRef, useState } from "react";
import { messageOf, useCreateFolder } from "../lib/queries.ts";
import dialog from "./ConfirmDialog.module.css";
import filePage from "./file/FilePage.module.css";
import { Icon } from "./Icon.tsx";
import ui from "./ui.module.css";

type Props = { parentId: string | null; className?: string; variant?: "primary" | "frame" };

export function NewFolderButton({ parentId, className, variant }: Props) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        className={`${ui.button} ${className ?? ""}`}
        data-variant={variant}
        onClick={() => setOpen(true)}
      >
        <Icon name="folderPlus" />
        New folder
      </button>
      {open ? <NewFolderDialog parentId={parentId} onClose={() => setOpen(false)} /> : null}
    </>
  );
}

function NewFolderDialog({ parentId, onClose }: { parentId: string | null; onClose: () => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  const create = useCreateFolder(parentId);
  const [name, setName] = useState("");
  const titleId = useId();
  const inputId = useId();
  const trimmed = name.trim();
  const slash = trimmed.includes("/");

  useEffect(() => {
    ref.current?.showModal();
  }, []);

  const dismiss = () => {
    ref.current?.close();
    onClose();
  };

  const close = () => {
    if (!create.isPending) dismiss();
  };

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: Escape reaches the dialog as its cancel event
    <dialog
      ref={ref}
      className={dialog.dialog}
      aria-labelledby={titleId}
      onCancel={(event) => {
        event.preventDefault();
        close();
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget) close();
      }}
    >
      <form
        className={`${dialog.body} ${dialog.form}`}
        onSubmit={(event) => {
          event.preventDefault();
          if (trimmed === "" || slash) return;
          create.mutate(trimmed, { onSuccess: dismiss });
        }}
      >
        <span className={dialog.candyBlob} aria-hidden="true">
          <Icon name="folder" />
        </span>
        <h2 id={titleId} className={dialog.title}>
          New folder
        </h2>
        <div className={dialog.field}>
          <label htmlFor={inputId} className={dialog.fieldLabel}>
            Name
          </label>
          <input
            id={inputId}
            className={ui.input}
            value={name}
            maxLength={255}
            placeholder="Mixes"
            autoComplete="off"
            spellCheck={false}
            autoFocus
            aria-invalid={slash || create.error ? true : undefined}
            onChange={(event) => {
              setName(event.currentTarget.value);
              if (create.error) create.reset();
            }}
          />
          {slash ? (
            <p className={filePage.fieldError} role="alert">
              A folder name cannot contain “/”.
            </p>
          ) : null}
        </div>
        {create.error ? (
          <p className={dialog.error} role="alert">
            {messageOf(create.error)}
          </p>
        ) : null}
        <div className={dialog.actions}>
          <button type="button" className={ui.button} onClick={close} disabled={create.isPending}>
            Cancel
          </button>
          <button
            type="submit"
            className={ui.button}
            data-variant="primary"
            disabled={create.isPending || trimmed === "" || slash}
          >
            {create.isPending ? "Creating…" : "Create folder"}
          </button>
        </div>
      </form>
    </dialog>
  );
}
